<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import Draggable from 'vuedraggable';
import SectionCard from '../SectionCard.vue';
import RealEstateIcon from '../RealEstateIcon.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import { money, formatSize, sizeUnitLabel } from '../helpers';
import {
  apiErrorCode,
  apiErrorMessage,
} from '../../../../api/realEstate/errors';

export default {
  name: 'FloorPlansSection',
  components: { SectionCard, RealEstateIcon, Draggable, DeleteModal },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localUnits: [...this.project.units],
      isReordering: false,
      unitToDelete: null,
      isDeleting: false,
      blockedUnit: null,
    };
  },
  computed: {
    selectedCode: {
      // Falls back to the first plan when ?plan= names one that no longer
      // exists (e.g. the selected plan was just deleted).
      get() {
        const { plan } = this.$route.query;
        if (plan && this.project.units.some(u => u.floor_plan_code === plan)) {
          return plan;
        }
        return this.project.units[0]?.floor_plan_code || null;
      },
      set(code) {
        this.$router.replace({ query: { ...this.$route.query, plan: code } });
      },
    },
    selectedUnit() {
      return (
        this.project.units.find(u => u.floor_plan_code === this.selectedCode) ||
        null
      );
    },
    imageTiles() {
      const unit = this.selectedUnit;
      return [
        {
          key: 'image',
          url: unit.image_url,
          label: this.$t('REAL_ESTATE.PLANS.UNIT_IMAGE'),
          emptyText: this.$t('REAL_ESTATE.PLANS.NO_UNIT_IMAGE'),
          fit: 'object-cover',
        },
        {
          key: 'layout',
          url: unit.layout_url,
          label: this.$t('REAL_ESTATE.PLANS.LAYOUT'),
          emptyText: this.$t('REAL_ESTATE.PLANS.NO_LAYOUT'),
          fit: 'object-contain bg-white',
        },
      ];
    },
  },
  watch: {
    'project.units': function onUnitsChange(units) {
      this.localUnits = [...units];
    },
  },
  methods: {
    ...mapActions('realEstate', ['removeUnit', 'reorderProjectUnits']),
    money,
    formatSize,
    unitLabel(unit) {
      return sizeUnitLabel(this, unit);
    },
    async handleReorder() {
      this.isReordering = true;
      try {
        await this.reorderProjectUnits({
          propertyId: this.project.property_id,
          unitIds: this.localUnits.map(u => u.unit_id),
        });
        useAlert(this.$t('REAL_ESTATE.PLANS.SUCCESS_REORDERED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.PLANS.ERROR_REORDER'));
        this.localUnits = [...this.project.units];
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isReordering = false;
      }
    },
    handleDelete(unit) {
      this.blockedUnit = null;
      this.unitToDelete = unit;
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.removeUnit({
          propertyId: this.project.property_id,
          unitId: this.unitToDelete.unit_id,
        });
        useAlert(this.$t('REAL_ESTATE.PLANS.SUCCESS_DELETED'));
        this.unitToDelete = null;
      } catch (err) {
        if (apiErrorCode(err) === 'has_appointments') {
          this.blockedUnit = this.unitToDelete;
        } else {
          useAlert(
            apiErrorMessage(this, err, 'REAL_ESTATE.PLANS.ERROR_DELETE')
          );
        }
        this.unitToDelete = null;
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <SectionCard section-id="plans" :title="$t('REAL_ESTATE.PLANS.TITLE')">
    <template #action>
      <woot-button
        variant="clear"
        size="small"
        icon="add"
        @click="$emit('add')"
      >
        {{ $t('REAL_ESTATE.PLANS.ADD_BUTTON') }}
      </woot-button>
    </template>

    <DeleteModal
      v-if="unitToDelete"
      :delete-name="unitToDelete.floor_plan_code"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
      @close="unitToDelete = null"
    />

    <p
      v-if="blockedUnit"
      class="mb-4 flex items-center gap-2 rounded-md bg-amber-50 text-amber-700 text-sm px-3 py-2"
    >
      <fluent-icon icon="warning" size="14" />
      <span>
        {{
          $t('REAL_ESTATE.PLANS.HAS_APPOINTMENTS_WARNING', {
            code: blockedUnit.floor_plan_code,
          })
        }}
      </span>
      <router-link
        :to="{
          name: 'real_estate_appointments',
          query: { property: project.property_id },
        }"
        class="text-woot-500 hover:underline font-medium shrink-0"
      >
        {{ $t('REAL_ESTATE.PLANS.HAS_APPOINTMENTS_LINK') }}
      </router-link>
    </p>

    <p v-if="!project.units.length" class="text-sm text-slate-400">
      {{ $t('REAL_ESTATE.PLANS.EMPTY') }}
    </p>

    <template v-else>
      <Draggable
        v-model="localUnits"
        tag="div"
        class="flex flex-wrap gap-2 mb-4"
        :disabled="isReordering"
        @end="handleReorder"
      >
        <button
          v-for="unit in localUnits"
          :key="unit.unit_id"
          type="button"
          class="px-3 py-1.5 rounded-md text-sm font-medium border transition-colors cursor-move"
          :class="
            unit.floor_plan_code === selectedCode
              ? 'bg-woot-500 border-woot-500 text-white'
              : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
          "
          @click="selectedCode = unit.floor_plan_code"
        >
          {{ unit.floor_plan_code }}
        </button>
      </Draggable>

      <div v-if="selectedUnit" class="space-y-3">
        <div class="flex items-center justify-between">
          <p class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ money(selectedUnit.price_from, selectedUnit.currency) }}
            {{ $t('REAL_ESTATE.PLANS.PRICE_ONWARDS') }}
          </p>
          <div class="flex gap-1">
            <woot-button
              variant="clear"
              size="small"
              icon="edit"
              @click="$emit('edit', selectedUnit)"
            />
            <woot-button
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              @click="handleDelete(selectedUnit)"
            />
          </div>
        </div>

        <div
          class="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-300"
        >
          <span
            v-if="selectedUnit.built_up_size != null"
            class="flex items-center gap-1"
          >
            <RealEstateIcon icon="ruler" :size="15" />
            {{ formatSize(selectedUnit.built_up_size) }}
            {{ unitLabel(selectedUnit.built_up_unit) }}
          </span>
          <span v-if="selectedUnit.bedrooms" class="flex items-center gap-1">
            <RealEstateIcon icon="bed" :size="15" />
            {{ selectedUnit.bedrooms_label }}
          </span>
          <span v-if="selectedUnit.bathrooms" class="flex items-center gap-1">
            <RealEstateIcon icon="bath" :size="15" />
            {{ selectedUnit.bathrooms_label }}
          </span>
          <span
            v-if="selectedUnit.parking_lots"
            class="flex items-center gap-1"
          >
            <RealEstateIcon icon="car" :size="15" />
            {{ selectedUnit.parking_lots }}
          </span>
        </div>

        <a
          v-if="selectedUnit.website_url"
          :href="selectedUnit.website_url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-block text-sm text-woot-500 hover:underline"
        >
          {{ $t('REAL_ESTATE.PLANS.WEBSITE_LINK') }}
        </a>

        <!-- Unit image and layout drawing as thumbnails, each opening full
             size in a new tab. The layout is shown whole (object-contain):
             cropping a floor drawing hides rooms. -->
        <div class="grid grid-cols-2 gap-3 max-w-lg">
          <figure v-for="tile in imageTiles" :key="tile.key" class="m-0">
            <a
              v-if="tile.url"
              :href="tile.url"
              target="_blank"
              rel="noopener noreferrer"
              :title="
                $t('REAL_ESTATE.PLANS.OPEN_FULL_SIZE', { name: tile.label })
              "
              class="relative flex items-center justify-center aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:border-woot-500 dark:hover:border-woot-500 transition-colors"
            >
              <fluent-icon icon="image" size="22" />
              <img
                :src="tile.url"
                :alt="tile.label"
                class="absolute inset-0 w-full h-full"
                :class="tile.fit"
                @error="$event.target.remove()"
              />
            </a>
            <div
              v-else
              class="flex items-center justify-center aspect-[4/3] rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 px-3 text-center text-xs text-slate-400"
            >
              {{ tile.emptyText }}
            </div>
            <figcaption class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{ tile.label }}
            </figcaption>
          </figure>
        </div>
      </div>
    </template>
  </SectionCard>
</template>
